import { defineStore, type StoreGeneric, storeToRefs } from 'pinia';
import { ref, computed } from 'vue';
import { v4 as uuidv4 } from 'uuid';
import { NavigationHookAfter, useRouter } from 'vue-router';
import {
  AnalyticsInputTypesRegistry,
  FrontendsAnalyticsEventTypesRegistry,
  AppAnalyticsInfo,
  UserAnalyticsInfo,
} from '@ilteducation/analytics-gateway-types';
import { getAppInfo } from '@ilteducation/app-info';
import { useTokens, useUserContext } from '@ilteducation/ilt-auth';
import { getActiveOrganizationsIds, useMyOrganizations } from '@ilteducation/my-account';
import { useConfigHelper } from '@ilteducation/config';
import { postAnalyticsEvent, postAnonymousAnalyticsEvent } from './analytics-gateway-api';
import { toExternalReferrer } from './referrer';

const getHighestLevelRole = (userRoles: { type: string; value: string }[]): string => {
  const roleHierarchyFromLowestToHighest = ['guest', 'shared', 'student', 'guardian', 'employee', 'admin'];

  let highestRole = userRoles.length ? userRoles[0].value.toLowerCase() : 'Not available';
  for (const role of userRoles) {
    const roleName = role.value.toLowerCase();

    if (roleHierarchyFromLowestToHighest.indexOf(roleName) > roleHierarchyFromLowestToHighest.indexOf(highestRole)) {
      highestRole = roleName;
    }
  }

  return highestRole;
};

export type ClientAnalyticsConfig = {
  getCurrentSubscriptionName: () => Promise<string>;
};

type AnalyticsSessionFields = {
  appSessionId: string;
  eventTimestamp: string;
  referrer?: string;
};

type CommonFieldsSentWithEveryEventFromTheClient = AppAnalyticsInfo &
  UserAnalyticsInfo &
  AnalyticsSessionFields & { realm: string };

type DistributiveOmit<T, K extends PropertyKey> = T extends unknown ? Omit<T, K> : never;

/**
 * The public route accepts one event shape. Anything an app sends while nobody is identified is
 * folded into it: the event's own name when it has one, the input type otherwise, and every field
 * that does not fit kept as metadata rather than dropped.
 */
const asAnonymousEvent = (type: string, eventSpecificData: object) => {
  const { eventName, routeName, previousRouteName, metadata, ...rest } = eventSpecificData as Record<string, unknown>;

  return {
    eventName: typeof eventName === 'string' ? eventName : type,
    routeName: typeof routeName === 'string' ? routeName : undefined,
    previousRouteName: typeof previousRouteName === 'string' ? previousRouteName : undefined,
    metadata: { ...(metadata as object), ...rest },
  };
};

const UNKNOWN_SUBSCRIPTION_MARKER = 'UNKNOWN_SUBSCRIPTION';

// Idle time that ends a session. 30 minutes is the web-analytics convention, and long enough to
// cover a login round trip, a reading sitting or a lesson interruption.
const SESSION_IDLE_TIMEOUT_MS = 30 * 60 * 1000;
let clientAnalyticsConfig: ClientAnalyticsConfig = {
  getCurrentSubscriptionName: async () => UNKNOWN_SUBSCRIPTION_MARKER,
};

export const useClientAnalytics = defineStore(
  'client-analytics',
  () => {
    const isInitialized = ref(false);
    const clientSessionId = ref();

    /*
    External origin the current session was entered from. Persisted with the session, so the login
    round trip - which reloads the page with the auth or SSO provider as document.referrer - keeps
    the original entry point instead of overwriting it.
     */
    const sessionReferrer = ref<string | undefined>();

    /*
    Last timestamp (in milliseconds) when an event for this session id was sent.
     */
    const clientSessionIdUsedAtTSMillis = ref<number | null>(null);

    const appAnalyticsInfo = ref<AppAnalyticsInfo | null>(null);

    const router = useRouter();

    const userContext = useUserContext();
    const { isLoggedIn, user, roles, realm } = storeToRefs(userContext as unknown as StoreGeneric);

    const { organizations, loadMyOrganizations } = useMyOrganizations(false);
    const activeOrgIds = computed(() => getActiveOrganizationsIds(organizations.value.data));

    /*
     Only initializeAnalytics passes a referrer: document.referrer belongs to the page load, so a
     session that starts later, after an idle tab comes back, does not claim one from hours ago.
     */
    const generateNewSessionId = (referrer?: string) => {
      const now = Date.now();

      /*
       A session ends after SESSION_IDLE_TIMEOUT_MS without a single event, not when the page
       reloads or the tab is hidden - someone reading a book, logging in or coming back to the tab
       after lunch is still the same visit. Every event pushes the window out, whoever sent it, so a
       deeplink arrival and the events after the login wall share one session ID for free.
       */
      const lastUsedAt = clientSessionIdUsedAtTSMillis.value;

      if (clientSessionId.value && lastUsedAt !== null && now - lastUsedAt < SESSION_IDLE_TIMEOUT_MS) {
        return;
      }

      clientSessionId.value = uuidv4();
      clientSessionIdUsedAtTSMillis.value = now;
      sessionReferrer.value = referrer;
    };

    const updateSessionLastUsedTimestamp = () => {
      clientSessionIdUsedAtTSMillis.value = Date.now();
    };

    const navigationHookAfter: NavigationHookAfter = (to, from, failure) => {
      if (failure) {
        return;
      }

      sendAnalyticsEvent('AppUserEventType', {
        eventName: 'PageNavigation',
        metadata: {
          to: to.fullPath,
          from: from.fullPath,
        },
        routeName: to.name ? String(to.name) : undefined,
        previousRouteName: from.name ? String(from.name) : undefined,
      });
    };

    const onDocumentVisible = () => {
      generateNewSessionId();

      sendAnalyticsEvent('AppUserEventType', { eventName: 'PageBecameVisible', metadata: {} });
    };

    const onDocumentHidden = () => {
      sendAnalyticsEvent('AppUserEventType', { eventName: 'PageHidden', metadata: {} }, true);
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        onDocumentVisible();
      }

      if (document.visibilityState === 'hidden') {
        onDocumentHidden();
      }
    };

    const initializeAnalytics = (config: ClientAnalyticsConfig) => {
      if (isInitialized.value) {
        return;
      }

      clientAnalyticsConfig = config;

      generateNewSessionId(toExternalReferrer(document.referrer, location.hostname));

      isInitialized.value = true;

      document.addEventListener('visibilitychange', onVisibilityChange);

      router.afterEach(navigationHookAfter);
    };

    const loadAppAnalyticsInfo = async () => {
      const appInfo = await getAppInfo();

      appAnalyticsInfo.value = {
        appVersion: appInfo.version,
        appName: appInfo.name,
        platform: appInfo.platform,
      };
    };

    const loadUserAnalyticsInfo = async (): Promise<UserAnalyticsInfo> => {
      const userId = user.value.uid;

      const userRole = getHighestLevelRole(roles.value);

      // We wait for organizations to load before we send analytics events.
      await loadMyOrganizations();

      /*
       We just use the first organization - this may not be accurate for users with multiple organizations,
       but we currently don't have a better way to determine which organization to use for analytics purposes.
        */
      const organizationId = activeOrgIds.value.length ? activeOrgIds.value[0] : 'UNKNOWN_ORGANIZATION_ID';

      const userCurrentSubscription = await clientAnalyticsConfig.getCurrentSubscriptionName();

      return {
        userId,
        userRole,
        userCurrentSubscription: userCurrentSubscription || UNKNOWN_SUBSCRIPTION_MARKER,
        organizationId,
        realm: realm.value,
      };
    };

    const analyticsToBeInitialized = async (): Promise<boolean> => {
      const maxWaitTimeMs = 10 * 1000; // 10 seconds
      const startTime = Date.now();

      while (!isInitialized.value) {
        if (Date.now() - startTime > maxWaitTimeMs) {
          return false;
        }
        await new Promise((resolve) => setTimeout(resolve, 100)); // wait for 100ms before checking again
      }

      return true;
    };

    /**
     * Sends an analytics event to the analytics gateway. Automatically adds common fields such as app and user info, session id, etc.
     * Note that this function does not check if the page is currently visible or not - use the isSentWhenPageIsHidden parameter to indicate if the event should be sent even when the page is hidden (e.g. in case of page unload or visibility change events).
     * @param type
     * @param eventSpecificData
     * @param isSentWhenPageIsHidden
     */
    const sendAnalyticsEvent = async <T extends keyof FrontendsAnalyticsEventTypesRegistry>(
      type: T,
      eventSpecificData: DistributiveOmit<
        FrontendsAnalyticsEventTypesRegistry[T],
        keyof CommonFieldsSentWithEveryEventFromTheClient
      >,
      isSentWhenPageIsHidden: boolean = false,
    ) => {
      const isConfigInitialized = await analyticsToBeInitialized();
      if (!isConfigInitialized) {
        return;
      }

      if (!clientSessionId.value) {
        generateNewSessionId();
      }

      updateSessionLastUsedTimestamp();

      if (!appAnalyticsInfo.value) {
        await loadAppAnalyticsInfo();
      }

      const commonFields = {
        ...appAnalyticsInfo.value!,
        eventTimestamp: new Date().toISOString(),
        appSessionId: clientSessionId.value!,
        referrer: sessionReferrer.value,
      };

      /*
       isLoggedIn alone is not enough on /login/skolon, so the token decides. No token means nobody
       to describe: the event goes to the public route with no user envelope on it, which is how a
       visitor still at the login wall stays visible.
       */
      const tokensStore = useTokens();
      const tokens = isLoggedIn.value ? await tokensStore.getValidTokens() : null;

      if (!tokens) {
        const { getOptionalRealm } = useConfigHelper();

        await postAnonymousAnalyticsEvent(
          {
            ...commonFields,
            ...asAnonymousEvent(type, eventSpecificData),
            realm: getOptionalRealm() ?? undefined,
          },
          isSentWhenPageIsHidden,
        );

        return;
      }

      const userAnalyticsInfo = await loadUserAnalyticsInfo();

      // @ts-ignore eventSpecificData should contain all required fields. I don't know how to make typescript happy.
      const eventData: AnalyticsInputTypesRegistry[T] = {
        ...commonFields,
        ...userAnalyticsInfo,
        ...eventSpecificData,
      };

      await postAnalyticsEvent(type, eventData, isSentWhenPageIsHidden);
    };

    return {
      initializeAnalytics,
      sendAnalyticsEvent,

      // These are exported just for persistence purposes, should not be used directly
      clientSessionId,
      clientSessionIdUsedAtTSMillis: clientSessionIdUsedAtTSMillis,
      sessionReferrer,
    };
  },
  {
    // @ts-ignore Part of persistence plugin
    persist: {
      storage: localStorage,
      pick: ['clientSessionId', 'clientSessionIdUsedAtTSMillis', 'sessionReferrer'],
    },
  },
);
